/**
 * Speech Synthesis Service
 *
 * Provides text-to-speech capabilities using the Web Speech API.
 */

import { isSpeechSynthesisSupported } from './feature-detection';

export interface SpeechSynthesisConfig {
  /** Voice language (e.g., 'en-US', 'es-ES') */
  language?: string;
  /** Speech rate (0.1 to 10, default: 1) */
  rate?: number;
  /** Voice pitch (0 to 2, default: 1) */
  pitch?: number;
  /** Volume (0 to 1, default: 1) */
  volume?: number;
  /** Preferred voice name */
  voiceName?: string;
}

export interface SpeechSynthesisCallbacks {
  /** Called when speech starts */
  onStart?: () => void;
  /** Called when speech ends */
  onEnd?: () => void;
  /** Called on error */
  onError?: (error: string) => void;
  /** Called when speech is paused */
  onPause?: () => void;
  /** Called when speech resumes */
  onResume?: () => void;
}

/**
 * Speech Synthesis Service
 */
export class SpeechSynthesisService {
  private config: SpeechSynthesisConfig;
  private _isSpeaking = false;
  private callbacks: SpeechSynthesisCallbacks | null = null;
  private cachedVoices: SpeechSynthesisVoice[] = [];

  constructor(config: SpeechSynthesisConfig = {}) {
    this.config = {
      language: 'en-US',
      rate: 1,
      pitch: 1,
      volume: 1,
      ...config,
    };

    // Pre-fetch voices (they may not be available immediately)
    if (this.isSupported()) {
      this.loadVoices();
      // Voices may load asynchronously
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  /**
   * Check if speech synthesis is supported
   */
  isSupported(): boolean {
    return isSpeechSynthesisSupported();
  }

  /**
   * Check if currently speaking
   */
  get isSpeaking(): boolean {
    return this._isSpeaking;
  }

  /**
   * Set the speech language
   */
  setLanguage(language: string): void {
    this.config.language = language;
  }

  /**
   * Get available voices
   */
  getVoices(): SpeechSynthesisVoice[] {
    return this.cachedVoices;
  }

  /**
   * Get voices for a specific language
   */
  getVoicesForLanguage(langCode: string): SpeechSynthesisVoice[] {
    const normalizedLang = langCode.toLowerCase();
    return this.cachedVoices.filter(
      voice => voice.lang.toLowerCase().startsWith(normalizedLang)
    );
  }

  /**
   * Speak text
   */
  speak(text: string, callbacks?: SpeechSynthesisCallbacks): boolean {
    if (!this.isSupported()) {
      callbacks?.onError?.('Speech synthesis is not supported in this browser');
      return false;
    }

    // Stop any current speech
    this.stop();

    this.callbacks = callbacks || null;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = this.config.language || 'en-US';
    utterance.rate = this.config.rate || 1;
    utterance.pitch = this.config.pitch || 1;
    utterance.volume = this.config.volume || 1;

    // Set voice if specified
    const voice = this.findVoice();
    if (voice) {
      utterance.voice = voice;
    }

    utterance.onstart = () => {
      this._isSpeaking = true;
      this.callbacks?.onStart?.();
    };

    utterance.onend = () => {
      this._isSpeaking = false;
      this.callbacks?.onEnd?.();
    };

    utterance.onerror = (event) => {
      this._isSpeaking = false;
      this.callbacks?.onError?.(event.error || 'Speech synthesis error');
    };

    utterance.onpause = () => {
      this.callbacks?.onPause?.();
    };

    utterance.onresume = () => {
      this.callbacks?.onResume?.();
    };

    try {
      window.speechSynthesis.speak(utterance);
      return true;
    } catch (error) {
      this._isSpeaking = false;
      callbacks?.onError?.('Failed to start speech synthesis');
      return false;
    }
  }

  /**
   * Stop speaking
   */
  stop(): void {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    this._isSpeaking = false;
  }

  /**
   * Pause speech
   */
  pause(): void {
    if (window.speechSynthesis && this._isSpeaking) {
      window.speechSynthesis.pause();
    }
  }

  /**
   * Resume speech
   */
  resume(): void {
    if (window.speechSynthesis) {
      window.speechSynthesis.resume();
    }
  }

  /**
   * Load available voices
   */
  private loadVoices(): void {
    if (window.speechSynthesis) {
      this.cachedVoices = window.speechSynthesis.getVoices();
    }
  }

  /**
   * Find the best matching voice
   */
  private findVoice(): SpeechSynthesisVoice | null {
    if (this.cachedVoices.length === 0) {
      this.loadVoices();
    }

    // Try to find specified voice by name
    if (this.config.voiceName) {
      const namedVoice = this.cachedVoices.find(
        v => v.name.toLowerCase().includes(this.config.voiceName!.toLowerCase())
      );
      if (namedVoice) return namedVoice;
    }

    // Find voice by language
    const lang = this.config.language || 'en-US';
    const langVoices = this.getVoicesForLanguage(lang);

    // Prefer default voice for the language
    const defaultVoice = langVoices.find(v => v.default);
    if (defaultVoice) return defaultVoice;

    // Prefer local voices over network
    const localVoice = langVoices.find(v => v.localService);
    if (localVoice) return localVoice;

    // Return first matching voice
    return langVoices[0] || null;
  }

  /**
   * Destroy the service
   */
  destroy(): void {
    this.stop();
    this.callbacks = null;
  }
}
